import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { designImages } from '../../data/designImages';
import ScrollToTop from '../../components/ScrollToTop';
import SortDropdown from '../../components/SortDropdown';
import FilterPopup from '../../components/FilterPopup';

// Sort states enum
const SORT_STATES = {
  ASC: 'asc',
  DESC: 'desc'
};

function Designs() {
  const navigate = useNavigate();
  const [selectedSoldOutImage, setSelectedSoldOutImage] = useState(null);
  const [priceSort, setPriceSort] = useState(() => {
    // Load sort order from localStorage on component mount
    const savedSort = localStorage.getItem('moolmit-price-sort');
    return savedSort === SORT_STATES.DESC ? SORT_STATES.DESC : SORT_STATES.ASC;
  });
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [filters, setFilters] = useState(() => {
    // Load filters from localStorage on component mount
    const savedFilters = localStorage.getItem('moolmit-filters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (e) {
        console.warn('Failed to parse saved filters, using defaults');
      }
    }
    return {
      all: true,
      available: false,
      soldOut: false,
      blackInk: false,
      colorInk: false
    };
  });

  const handleImageLoad = (designId) => {
    setLoadedImages(prev => new Set([...prev, designId]));
  };

  const handleImageClick = (design) => {
    if (design.status === 'soldOut') {
      setSelectedSoldOutImage(design);
    } else {
      navigate(`/design/${design.id}`);
    }
  };

  // Helper function to extract numeric price value
  const getPriceValue = (price) => {
    if (!price || price === "") return -1; // Use -1 for designs without prices to sort them last
    const match = price.match(/\$(\d+)/);
    return match ? parseInt(match[1], 10) : -1;
  };

  const filteredDesigns = useMemo(() => {
    let filtered = [...designImages];

    // Apply filters
    if (!filters.all) {
      let availabilityFiltered = filtered;
      
      // Handle availability filters
      // If neither filter is selected, default to showing only available designs
      if (!filters.available && !filters.soldOut) {
        availabilityFiltered = filtered.filter(design => !design.status);
      } else {
        availabilityFiltered = filtered.filter(design => {
          if (filters.available && !design.status) return true;
          if (filters.soldOut && design.status === 'soldOut') return true;
          return false;
        });
      }

      // Handle ink color filters
      let colorFiltered = availabilityFiltered;
      if (filters.blackInk || filters.colorInk) {
        colorFiltered = availabilityFiltered.filter(design => {
          if (filters.blackInk && design.isColor === false) return true;
          if (filters.colorInk && design.isColor === true) return true;
          return false;
        });
      }

      filtered = colorFiltered;
    }

    // Separate available and sold out items for sorting
    const available = filtered.filter(design => !design.status);
    const soldOut = filtered.filter(design => design.status === 'soldOut');

    // Always sort available items by price
    available.sort((a, b) => {
      const priceA = getPriceValue(a.price);
      const priceB = getPriceValue(b.price);
      
      // Handle cases where one or both prices are -1 (no price)
      if (priceA === -1 && priceB === -1) return 0;
      if (priceA === -1) return 1; // No price items go last
      if (priceB === -1) return -1;
      
      return priceSort === SORT_STATES.ASC ? priceA - priceB : priceB - priceA;
    });

    // Combine sorted available items with sold out items
    return [...available, ...soldOut];
  }, [filters, priceSort]);

  // Save sort order to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('moolmit-price-sort', priceSort);
  }, [priceSort]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('moolmit-filters', JSON.stringify(filters));
  }, [filters]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-2">
      
      {/* Filter Buttons */}
      <div className="sticky top-16 bg-white/95 backdrop-blur-sm z-30 py-4 -mx-4 px-4 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-shrink min-w-0">
            <SortDropdown currentSort={priceSort} onSortChange={setPriceSort} />
            <FilterPopup currentFilters={filters} onFilterChange={setFilters} />
          </div>

          {/* <button
            onClick={() => navigate('/booking')}
            className="px-4 sm:px-6 py-2 rounded-full border-2 border-black bg-black text-white font-medium hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap"
          >
            Book Now
          </button> */}
        </div>
      </div>
      
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-3 md:gap-5">
        {filteredDesigns.map((design) => (
          <div key={design.id}>
            <div 
              className={`w-full pt-[100%] relative cursor-pointer overflow-hidden max-w-[250px] mx-auto bg-white ${design.status === 'soldOut' ? 'opacity-50' : ''}`}
              onClick={() => handleImageClick(design)}
            >
              {/* Skeleton placeholder */}
              {!loadedImages.has(design.id) && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
              )}
              
              <img
                src={design.image}
                alt={design.title}
                className={`absolute top-0 left-0 w-full h-full object-contain hover:scale-105 transition-all duration-300 select-none ${
                  loadedImages.has(design.id) ? 'opacity-100' : 'opacity-0'
                }`}
                onContextMenu={(e) => e.preventDefault()}
                onLoad={() => handleImageLoad(design.id)}
                draggable="false"
                loading="lazy"
                style={{
                  WebkitTouchCallout: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Modal for sold out designs */}
      {selectedSoldOutImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setSelectedSoldOutImage(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] w-full mx-4 flex flex-col items-center">
            <div className="relative w-full">
              <span className="absolute top-4 left-4 px-4 py-1 bg-white text-black rounded-full text-sm border border-black z-10">
                Sold Out
              </span>
              <div className="relative">
                <img
                  src={selectedSoldOutImage.image}
                  alt={selectedSoldOutImage.title}
                  className="w-full h-full object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="absolute inset-0 bg-white opacity-50" />
              </div>
            </div>
            <button
              className="absolute top-4 right-4 text-white text-2xl"
              onClick={() => setSelectedSoldOutImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <ScrollToTop />
    </div>
  );
}

export default Designs;